import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { CalendarClock, Megaphone, Pencil, Pin, Plus, Trash2 } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DatePicker } from '@/components/ui/DatePicker';
import { DonutChart, type DonutSlice } from '@/components/ui/DonutChart';
import { EmptyState } from '@/components/ui/EmptyState';
import { QueryError } from '@/components/ui/QueryError';
import { StatCard } from '@/components/ui/StatCard';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { CONSOLE_PALETTE } from '@/features/institute/theme';
import { NOTICE_TYPES } from '@/features/teacher/types/notices';
import type { InstituteNoticeKind } from '../schemas/instituteNotice.schema';
import { formatInstituteDate, instituteToday } from '@/features/teacher/utils';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { ApiRequestError } from '@/lib/apiClient';
import { formatTimeAgo } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { InstituteNotice, NoticeAudience } from '@/types/instituteNotice';
import { instituteNoticesApi } from '../api/instituteNotices.api';
import { InstituteNoticeFormModal } from '../components/InstituteNoticeFormModal';
import { AUDIENCE_ORDER, AUDIENCE_STYLE, noticeAnchorId } from '../constants';

/**
 * The board's filters, as they sit in the address bar, so a refresh or coming
 * back from elsewhere finds the same day and audience.
 */
type Filters = {
  date: string;
  audience: NoticeAudience | '';
};

const DEFAULT_FILTERS: Filters = { date: '', audience: '' };

/** The day is any date the picker allows; the audience is one of the three. */
const FILTER_OPTIONS = { allowed: { audience: AUDIENCE_ORDER } };

/** The console's panel, used by every block on this screen. */
const PANEL =
  'rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-[#111c2e] shadow-[0_12px_32px_-8px_rgba(15,23,42,0.12)]';

/** Mirrors `MAX_NOTICES_RETURNED` on the server: the board read is capped there. */
const NOTICES_RETURNED_AT_MOST = 100;

/** The three kinds, in the order the board is read in. */
const KIND_ORDER: InstituteNoticeKind[] = ['GENERAL', 'EVENT', 'REMINDER'];

/** The arc each kind takes in the ring, beside its shared label and dot. */
const KIND_STROKE: Record<InstituteNoticeKind, string> = {
  GENERAL: 'stroke-blue-500',
  EVENT: 'stroke-emerald-500',
  REMINDER: 'stroke-amber-500',
};

/** A titled block on the right rail. */
function RailPanel({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Megaphone;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className={cn(PANEL, 'flex flex-col gap-4 p-5')}>
      <header className="flex items-center gap-2.5">
        <span
          className="relative grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 via-indigo-600 to-sky-600 text-white shadow-sm"
          aria-hidden
        >
          <Icon className="relative h-4 w-4" />
          <span className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-sheen" />
        </span>
        <h2 className="min-w-0 flex-1 truncate text-sm font-black tracking-tight text-ink">
          {title}
        </h2>
      </header>
      {children}
    </section>
  );
}

/**
 * The institute's own notice board.
 *
 * What the office puts up for the whole institute — "closed on Monday", "annual
 * day on the 15th" — and who each notice is for. It is not the class board: a
 * teacher's notices are addressed to the sections they take and are posted from
 * the teacher console.
 *
 * Drawn as the board it is rather than as a table: a notice is a short piece of
 * writing somebody has to *read*, and a row four columns wide gives its title
 * two inches and its body none. Pinned cards give the words the room they need,
 * and the audience is the loudest thing on each one — the question the office
 * asks itself is always "who will actually see this".
 *
 * Read-only for anyone holding no `INSTITUTE_NOTICE_MANAGE`, which is most of
 * the staff room. The server enforces the same split — it decides `canWithdraw`
 * per notice, and the same key edits — so this only decides what is worth offering.
 *
 * The day filter is applied by the server, because the board carries back the
 * newest hundred and a notice from March must still be findable. The audience
 * is applied here, over what has already arrived, so it answers
 * instantly — and the figures above count everything that arrived, never just
 * what is left after filtering.
 */
export function InstituteNoticesPage() {
  const permissions = useAuthStore((state) => state.user?.permissions);
  const canManage = Boolean(permissions?.includes('INSTITUTE_NOTICE_MANAGE'));

  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const { date: onDate, audience } = filters;
  const setOnDate = (date: string) => setFilters({ date });
  const setAudience = (next: NoticeAudience | '') => setFilters({ audience: next });

  const { data, isLoading, error, refetch } = useApiQuery(
    () => instituteNoticesApi.list(onDate || undefined),
    [onDate],
  );

  const [isFormOpen, setIsFormOpen] = useState(false);
  /** The notice open in the form for editing; null while composing a new one. */
  const [editing, setEditing] = useState<InstituteNotice | null>(null);
  const [withdrawing, setWithdrawing] = useState<InstituteNotice | null>(null);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  /** The notice whose pin is on its way to the server, so it cannot be pressed twice. */
  const [pinningUuid, setPinningUuid] = useState<string | null>(null);

  const notices = useMemo(() => data?.notices ?? [], [data]);

  const visible = useMemo(
    () =>
      notices.filter((notice) => !audience || notice.audience === audience),
    [notices, audience],
  );

  const hasFilters = Boolean(onDate || audience);

  /**
   * Arriving from the dashboard with `#notice-<uuid>`: once the board has drawn,
   * bring that card into view and light it up for a moment, so the eye lands
   * on the notice that was clicked rather than on the top of the list.
   *
   * Once per hash — a refetch after a pin or a take-down must not drag the page
   * back to it. A notice no longer on the board simply leaves the page where it is.
   */
  const { hash } = useLocation();
  const [focusedUuid, setFocusedUuid] = useState<string | null>(null);
  const handledHash = useRef('');

  useEffect(() => {
    if (!hash || hash === handledHash.current || visible.length === 0) return;

    const target = visible.find((notice) => `#${noticeAnchorId(notice.uuid)}` === hash);
    if (!target) return;

    handledHash.current = hash;
    document
      .getElementById(noticeAnchorId(target.uuid))
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFocusedUuid(target.uuid);
  }, [hash, visible]);

  // Its own effect, so a refetch landing mid-highlight cannot cancel the fade.
  useEffect(() => {
    if (!focusedUuid) return;
    const timer = window.setTimeout(() => setFocusedUuid(null), 2500);
    return () => window.clearTimeout(timer);
  }, [focusedUuid]);

  const byAudience = useMemo(() => {
    const tally = {} as Record<NoticeAudience, number>;
    for (const key of AUDIENCE_ORDER) tally[key] = 0;
    for (const notice of notices) tally[notice.audience] += 1;
    return tally;
  }, [notices]);

  const byKind = useMemo(() => {
    const tally = {} as Record<InstituteNoticeKind, number>;
    for (const key of KIND_ORDER) tally[key] = 0;
    for (const notice of notices) tally[notice.noticeType] += 1;
    return tally;
  }, [notices]);

  const slices: DonutSlice[] = KIND_ORDER.map((key) => ({
    key,
    label: NOTICE_TYPES[key].label,
    value: byKind[key],
    stroke: KIND_STROKE[key],
    dot: NOTICE_TYPES[key].dot,
  })).filter((slice) => slice.value > 0);

  /**
   * The next few notices still ahead, soonest first — by the day a notice is
   * about, not the day it was written, so a past event never crowds out the
   * one coming up on Friday. Today counts as still ahead.
   */
  const upcoming = useMemo(() => {
    const today = instituteToday();
    return notices
      .filter((notice) => notice.noticeDate >= today)
      .sort((a, b) => a.noticeDate.localeCompare(b.noticeDate))
      .slice(0, 4);
  }, [notices]);

  function clearFilters() {
    setFilters({ date: '', audience: '' });
  }

  function openCompose() {
    setEditing(null);
    setIsFormOpen(true);
  }

  function openEdit(notice: InstituteNotice) {
    setEditing(notice);
    setIsFormOpen(true);
  }

  function handlePosted(posted: InstituteNotice) {
    setIsFormOpen(false);
    toast.success(
      editing ? `“${posted.title}” is updated` : `“${posted.title}” is up`,
      `${posted.audienceLabel} · ${NOTICE_TYPES[posted.noticeType].label}`,
    );
    setEditing(null);
    refetch();
  }

  async function togglePin(notice: InstituteNotice) {
    setPinningUuid(notice.uuid);
    try {
      const saved = await instituteNoticesApi.setPinned(notice.uuid, !notice.isPinned);
      toast.success(
        saved.isPinned ? 'Pinned to the top' : 'Unpinned',
        saved.isPinned
          ? `“${saved.title}” now leads the board for ${saved.audienceLabel.toLowerCase()}.`
          : `“${saved.title}” is back in date order.`,
      );
      refetch();
    } catch (caught) {
      toast.error(
        notice.isPinned ? 'Could not unpin that notice' : 'Could not pin that notice',
        caught instanceof ApiRequestError ? caught.message : 'Please try again.',
      );
    } finally {
      setPinningUuid(null);
    }
  }

  async function confirmWithdraw() {
    if (!withdrawing) return;
    setIsWithdrawing(true);

    try {
      await instituteNoticesApi.remove(withdrawing.uuid);
      toast.success('That notice is off the board', 'Nobody sees it from now on.');
      setWithdrawing(null);
      refetch();
    } catch (caught) {
      // The dialog closes first: a toast raised under an open dialog would be
      // hidden behind it.
      setWithdrawing(null);
      toast.error(
        'Could not take that notice down',
        caught instanceof ApiRequestError ? caught.message : 'Please try again.',
      );
    } finally {
      setIsWithdrawing(false);
    }
  }

  return (
    <div className={cn('flex flex-col gap-6 animate-hero-zoom', CONSOLE_PALETTE)}>
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">
            Notice board
          </h1>
          <p className="mt-0.5 text-sm font-medium text-slate-500 dark:text-slate-400">
            What your institute puts up for everybody. Staff see it on their dashboard, students
            in their panel.
          </p>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
          {canManage && (
            <button
              type="button"
              onClick={openCompose}
              className="rounded-xl bg-indigo-600 tracking-wide hover:bg-indigo-700 text-white px-4 py-2 text-sm font-bold inline-flex items-center gap-2 transition-all cursor-pointer shadow-sm shadow-indigo-500/20 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              <Plus className="h-4 w-4 stroke-[2.5]" aria-hidden />
              <span>Put up a notice</span>
            </button>
          )}
        </div>
      </header>

      {!canManage && (
        <Alert tone="info" title="Read only">
          Your role lets you read the institute notice board but not post to it. Ask your institute
          administrator if something needs putting up or taking down.
        </Alert>
      )}

      {/* Who the board is talking to. Each figure is the way into its own notices. */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4 animate-slide-down delay-50 w-full max-w-full">
        <StatCard
          label="On the board"
          value={notices.length}
          // The board is not kept by session, and the server returns at most
          // its latest hundred — so the hint says which of those this is.
          hint={
            onDate
              ? 'For the chosen day'
              : notices.length >= NOTICES_RETURNED_AT_MOST
                ? `Latest ${NOTICES_RETURNED_AT_MOST}`
                : 'All dates'
          }
          variant="slate"
          icon={<Pin className="h-5 w-5 text-white" />}
          isLoading={isLoading}
          onClick={() => {
            setAudience('');
          }}
          isActive={!audience}
          linkLabel="Show every notice"
        />

        {AUDIENCE_ORDER.map((key) => {
          const style = AUDIENCE_STYLE[key];
          const Icon = style.icon;
          const isShowing = audience === key;

          return (
            <StatCard
              key={key}
              label={style.short}
              value={byAudience[key]}
              hint={key === 'EVERYONE' ? 'Staff and students' : undefined}
              variant={style.variant}
              icon={<Icon className="h-5 w-5 text-white" />}
              isLoading={isLoading}
              onClick={() => setAudience(isShowing ? '' : key)}
              isActive={isShowing}
              linkLabel={
                isShowing ? 'Show every audience' : `Show only notices for ${style.short.toLowerCase()}`
              }
            />
          );
        })}
      </div>

      {error && (
        <QueryError title="Could not load the notice board" error={error} onRetry={refetch} />
      )}

      {!error && (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-12 animate-slide-down delay-100">
          <div className="flex flex-col gap-5 xl:col-span-8">
            {/* The day, and the way out of it. */}
            <div className="flex flex-col gap-2.5 rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50 dark:bg-[#0b1322] p-2.5 shadow-xs">
              <div className="flex flex-wrap items-end gap-2.5">
                <div className="w-full sm:w-48">
                  <DatePicker
                    label="Day it is about"
                    labelClassName="text-xs font-bold text-slate-700 dark:text-slate-200"
                    className="!h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
                    isClearable
                    placeholder="Any day"
                    value={onDate}
                    onChange={(event) => setOnDate(event.target.value)}
                  />
                </div>

                {/* No kind chips: General is the only kind the office posts, so
                    there is nothing left to choose between. */}
                {hasFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="!h-9 !py-1 !px-3 !text-[13px] font-bold border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-[#111c2e] text-slate-700 dark:text-slate-200 hover:bg-slate-50 hover:text-rose-600 hover:border-rose-300 shadow-2xs rounded-xl flex items-center gap-1.5 shrink-0 transition-colors cursor-pointer"
                  >
                    Clear filters
                  </button>
                )}
              </div>

              {hasFilters && (
                <p className="border-t border-slate-200/80 dark:border-slate-800 pt-2 text-[12px] font-bold text-slate-500 dark:text-slate-400">
                  Showing {visible.length} of {notices.length}
                </p>
              )}
            </div>

            {/* The board itself. Two columns of pinned cards rather than a table:
                a notice is something to read, and a four-column row gives its
                body nowhere to go. */}
            {isLoading ? (
              <div className={cn(PANEL, 'p-8 text-center text-xs font-medium text-ink-3')}>
                Reading the board…
              </div>
            ) : visible.length === 0 ? (
              <div className={cn(PANEL, 'p-4 sm:p-5')}>
                <EmptyState
                  icon={<Megaphone className="h-5 w-5" />}
                  title={hasFilters ? 'Nothing matches those filters' : 'Nothing on the board yet'}
                  description={
                    hasFilters
                      ? 'Clear the filters to see the whole board.'
                      : canManage
                        ? 'Put up your first notice — a holiday, an event, or anything the whole institute needs to know.'
                        : 'Your institute has not put anything up yet.'
                  }
                />
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {visible.map((notice, index) => {
                  const kindStyle = NOTICE_TYPES[notice.noticeType];
                  const audienceStyle = AUDIENCE_STYLE[notice.audience];
                  const AudienceIcon = audienceStyle.icon;

                  return (
                    <article
                      key={notice.uuid}
                      id={noticeAnchorId(notice.uuid)}
                      className={cn(
                        PANEL,
                        'relative flex flex-col gap-3 overflow-hidden p-5 pt-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl animate-card-pop',
                        focusedUuid === notice.uuid && '!border-indigo-500 dark:!border-indigo-400 ring-1 ring-inset ring-indigo-500/30 shadow-lg',
                        index % 4 === 1 && 'delay-50',
                        index % 4 === 2 && 'delay-100',
                        index % 4 === 3 && 'delay-180',
                      )}
                    >
                      {/* The kind, along the top edge — the card's own colour
                          before a word of it is read. */}
                      <span
                        className={cn('absolute inset-x-0 top-0 h-1.5', kindStyle.dot)}
                        aria-hidden
                      />
                      {/* The pin that holds it to the board. Whoever may take a
                          notice down may pin it too; everybody else sees the pin
                          only on a notice that is pinned. */}
                      {notice.canWithdraw ? (
                        <button
                          type="button"
                          aria-pressed={notice.isPinned}
                          aria-label={
                            notice.isPinned ? `Unpin ${notice.title}` : `Pin ${notice.title} to the top`
                          }
                          title={notice.isPinned ? 'Unpin' : 'Pin to the top'}
                          disabled={pinningUuid === notice.uuid}
                          onClick={() => void togglePin(notice)}
                          className={cn(
                            'absolute right-4 top-4 grid h-7 w-7 cursor-pointer place-items-center rounded-full border transition-all hover:scale-110',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line disabled:cursor-wait disabled:opacity-60',
                            notice.isPinned
                              ? cn(kindStyle.dot, 'border-transparent text-white')
                              : cn(kindStyle.bg, kindStyle.border, kindStyle.color),
                          )}
                        >
                          <Pin className={cn('h-3.5 w-3.5', notice.isPinned && 'fill-current')} aria-hidden />
                        </button>
                      ) : (
                        notice.isPinned && (
                          <span
                            className={cn(
                              'absolute right-4 top-4 grid h-7 w-7 place-items-center rounded-full text-white',
                              kindStyle.dot,
                            )}
                            title="Pinned"
                          >
                            <Pin className="h-3.5 w-3.5 fill-current" aria-hidden />
                            <span className="sr-only">Pinned</span>
                          </span>
                        )
                      )}

                      <div className="flex flex-wrap items-center gap-2 pr-8">
                        <span
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider',
                            audienceStyle.chip,
                          )}
                        >
                          <AudienceIcon className="h-3 w-3" aria-hidden />
                          {notice.audienceLabel}
                        </span>
                        <span
                          className={cn(
                            'rounded-full border px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider',
                            kindStyle.bg,
                            kindStyle.border,
                            kindStyle.color,
                          )}
                        >
                          {kindStyle.label}
                        </span>
                        {notice.isPinned && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-line bg-surface-2 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-ink-2">
                            <Pin className="h-3 w-3" aria-hidden />
                            Pinned
                          </span>
                        )}
                      </div>

                      <div className="min-w-0">
                        <h3 className="text-[15px] font-black leading-snug tracking-tight text-ink">
                          {notice.title}
                        </h3>
                        {notice.body && (
                          <p className="mt-1.5 whitespace-pre-line text-xs font-medium leading-relaxed text-ink-2">
                            {notice.body}
                          </p>
                        )}
                      </div>

                      <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-line pt-3">
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-ink-2">
                          <CalendarClock className="h-3.5 w-3.5 text-ink-3" aria-hidden />
                          {formatInstituteDate(notice.noticeDate)}
                        </span>
                        <span className="truncate text-[11px] font-medium text-ink-3">
                          By {notice.postedBy} · {formatTimeAgo(notice.createdAt)}
                        </span>

                        {notice.canWithdraw && (
                          <button
                            type="button"
                            aria-label={`Edit ${notice.title}`}
                            title="Edit"
                            onClick={() => openEdit(notice)}
                            className="ml-auto grid h-8 w-8 shrink-0 cursor-pointer place-items-center rounded-xl border border-indigo-200/80 dark:border-indigo-900/50 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 transition-all hover:scale-105 hover:bg-indigo-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
                          >
                            <Pencil className="h-3.5 w-3.5" aria-hidden />
                          </button>
                        )}

                        {notice.canWithdraw && (
                          <button
                            type="button"
                            aria-label={`Take down ${notice.title}`}
                            title="Take down"
                            onClick={() => setWithdrawing(notice)}
                            className="grid h-8 w-8 shrink-0 cursor-pointer place-items-center rounded-xl border border-rose-200/80 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-950/40 text-rose-500 dark:text-rose-400 transition-all hover:scale-105 hover:bg-rose-100"
                          >
                            <Trash2 className="h-3.5 w-3.5" aria-hidden />
                          </button>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>

          {/* The rail: what the board is made of, and what is coming up. */}
          <aside className="flex flex-col gap-5 xl:col-span-4">
            <RailPanel icon={Megaphone} title="What the board is made of">
              {slices.length === 0 ? (
                <p className="text-xs font-medium text-ink-3">
                  Nothing is up yet, so there is nothing to break down.
                </p>
              ) : (
                <DonutChart slices={slices} total={notices.length} caption="Notices" />
              )}
            </RailPanel>

            <RailPanel icon={CalendarClock} title="Coming up">
              {upcoming.length === 0 ? (
                <p className="text-xs font-medium text-ink-3">No notices are coming up.</p>
              ) : (
                <ul className="flex flex-col gap-3">
                  {upcoming.map((notice) => {
                    const style = AUDIENCE_STYLE[notice.audience];
                    const Icon = style.icon;

                    return (
                      <li key={notice.uuid} className="flex items-start gap-3">
                        <span
                          className={cn(
                            'grid h-8 w-8 shrink-0 place-items-center rounded-xl border',
                            style.chip,
                          )}
                          aria-hidden
                        >
                          <Icon className="h-3.5 w-3.5" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[12.5px] font-bold text-ink">
                            {notice.title}
                          </p>
                          <p className="truncate text-[11px] font-medium text-ink-3">
                            {notice.audienceLabel} · {formatInstituteDate(notice.noticeDate)}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </RailPanel>
          </aside>
        </div>
      )}

      <InstituteNoticeFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        notice={editing}
        onPosted={handlePosted}
      />

      <ConfirmDialog
        isOpen={Boolean(withdrawing)}
        tone="danger"
        title={`Take down “${withdrawing?.title ?? 'this notice'}”?`}
        description={
          withdrawing
            ? `Nothing is erased — the notice is kept in the institute's record, and the audit trail shows who took it down. ${withdrawing.audienceLabel} will simply stop seeing it.`
            : undefined
        }
        confirmLabel="Take it down"
        isLoading={isWithdrawing}
        onConfirm={confirmWithdraw}
        onCancel={() => setWithdrawing(null)}
      />
    </div>
  );
}
